import { autoinject, observable } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { Filters, filterTypes } from "elements/Filter";
import { PubSub } from "lib/event/PubSub";
import { FilterManager } from "lib/tables/FilterManager";
import { Models } from "models/core";
import { CustomerService } from "services/customer-service";
import { ProducerService } from "services/producer-service";
import { ServiceReportService } from "services/service-report-service";
import { ErrorService } from "services/error-service";
import {
  getShrinkUptakeFields,
  SHRINK_UPTAKE_SELECTED_COLUMNS_KEY,
  ShrinkUptakeField,
} from "./service-shrink-uptake-report-fields";

@autoinject
export class ServiceShrinkUptakeReport {
  private static readonly INCLUDE_SERVICE_DETAIL_KEY = 'service-shrink-uptake-report.include-service-detail';
  private static readonly INCLUDE_SERVICE_DETAIL_FIELD = '__includeServiceDetail';

  protected customers: Models.Customer[] = [];
  protected producers: Models.Producer[] = [];

  protected fields: ShrinkUptakeField[] = [];
  protected selectedFields: ShrinkUptakeField[] = [];
  protected displayFields: ShrinkUptakeField[] = [];

  protected tableData: Array<Record<string, unknown>> = [];
  protected includeServiceDetail = false;

  @observable
  protected ready = false;

  protected exportProgress = false;

  constructor(
    private t: I18N,
    protected fm: FilterManager,
    protected pubsub: PubSub,
    protected customerService: CustomerService,
    protected producerService: ProducerService,
    private serviceReportService: ServiceReportService,
    private errorService: ErrorService,
  ) {
    pubsub.sub('filter-manager:filter-changed', () => {
      void this.getShrinkUptakeReport();
    });

    // The column picker lives inside <filter-section>. When the user toggles a
    // column there, filter-section publishes this event with the updated fields
    // snapshot — we persist + refetch.
    pubsub.sub('filter-section:column-visibility-changed', (data) => {
      if (data.context !== 'service-shrink-uptake-report') return;
      this.updateSelectList();
    });
  }

  protected async attached() {
    await this.load();
    this.initFields();

    this.fm.init({
      context: 'service-shrink-uptake-report',
      storageKey: 'service-shrink-uptake-report',
      filters: {
        customerIds: {
          name: 'customerIds',
          label: this.t.tr('general.customer'),
          type: filterTypes.CHECKBOX,
          options: this.customers,
          query: 'customerIds',
        },
        producerIds: {
          name: 'producerIds',
          label: this.t.tr('general.producer'),
          type: filterTypes.CHECKBOX,
          options: this.producers,
          query: 'producerIds',
        }
      } satisfies Filters,
      defaultFilters: {
        customerIds: true,
        producerIds: true,
      }
    });
  }


  private async load() {
    const [customers, producers] = await Promise.all([
      this.customerService.getAll(),
      this.producerService.getAll(),
    ]);
    this.customers = customers;
    this.producers = producers;
  }

  private initFields() {
    this.fields = getShrinkUptakeFields();

    const includeServiceDetailStored = localStorage.getItem(ServiceShrinkUptakeReport.INCLUDE_SERVICE_DETAIL_KEY);
    this.includeServiceDetail = includeServiceDetailStored === 'true';

    this.fields.push({
      field: ServiceShrinkUptakeReport.INCLUDE_SERVICE_DETAIL_FIELD,
      title: 'general.includeServiceDetail',
      selected: this.includeServiceDetail,
    });

    const saved = localStorage.getItem(SHRINK_UPTAKE_SELECTED_COLUMNS_KEY);
    if (saved) {
      try {
        const selected = JSON.parse(saved) as string[];
        this.fields.forEach((f) => {
          if (f.field === ServiceShrinkUptakeReport.INCLUDE_SERVICE_DETAIL_FIELD) {
            return;
          }
          f.selected = selected.includes(f.field);
        });
      } catch {
        // ignore corrupt localstorage
      }
    }

    this.refreshSelectedFields();
  }

  private refreshSelectedFields() {
    this.selectedFields = this.fields.filter((f) =>
      f.selected && f.field !== ServiceShrinkUptakeReport.INCLUDE_SERVICE_DETAIL_FIELD,
    );
    this.displayFields = [...this.selectedFields];
  }

  private refreshDisplayFields() {
    if (!this.includeServiceDetail) {
      this.displayFields = [...this.selectedFields];
      return;
    }

    const dynamicFieldMap = new Map<string, ShrinkUptakeField>();
    const serviceLabel = this.t.tr('general.service') ?? 'Service';
    const serviceCountLabel = this.t.tr('net.numberofservices') ?? 'Service count';
    const suffixLabels: Record<string, string> = {
      MeasuredDate: this.t.tr('service.measuredDate') ?? 'Measured Date',
      CircumferenceMeasured: this.t.tr('service.circumferenceMeasuredShort') ?? 'Circumference Measured',
      ImpregnationType: this.t.tr('service.impregnationType') ?? 'Impregnation Type',
    };

    for (const row of this.tableData) {
      if (Object.prototype.hasOwnProperty.call(row, 'ServiceCount') && !dynamicFieldMap.has('ServiceCount')) {
        dynamicFieldMap.set('ServiceCount', {
          field: 'ServiceCount',
          title: 'net.numberofservices',
          rawTitle: serviceCountLabel,
          fromService: true,
        });
      }

      for (const key of Object.keys(row)) {
        if (!/^Service\d+(MeasuredDate|CircumferenceMeasured|ImpregnationType)$/.test(key)) {
          continue;
        }

        if (dynamicFieldMap.has(key)) {
          continue;
        }

        const match = /^Service(\d+)(MeasuredDate|CircumferenceMeasured|ImpregnationType)$/.exec(key);
        if (!match) {
          continue;
        }

        const serviceIndex = parseInt(match[1], 10);
        const suffix = match[2];
        const suffixText = suffixLabels[suffix] ?? suffix;

        dynamicFieldMap.set(key, {
          field: key,
          title: key,
          rawTitle: `${serviceLabel} ${serviceIndex} ${suffixText}`,
          isDate: suffix === 'MeasuredDate',
          fromService: true,
        });
      }
    }

    const serviceCountField = dynamicFieldMap.get('ServiceCount');
    const detailFields = [...dynamicFieldMap.values()]
      .filter((f) => f.field !== 'ServiceCount')
      .sort((a, b) => {
      const aMatch = /^Service(\d+)(MeasuredDate|CircumferenceMeasured|ImpregnationType)$/.exec(a.field);
      const bMatch = /^Service(\d+)(MeasuredDate|CircumferenceMeasured|ImpregnationType)$/.exec(b.field);
      if (!aMatch || !bMatch) return a.field.localeCompare(b.field);

      const aIndex = parseInt(aMatch[1], 10);
      const bIndex = parseInt(bMatch[1], 10);
      if (aIndex !== bIndex) return aIndex - bIndex;

      const order: Record<string, number> = {
        CircumferenceMeasured: 0,
        MeasuredDate: 1,
        ImpregnationType: 2,
      };

      return (order[aMatch[2]] ?? 999) - (order[bMatch[2]] ?? 999);
    });

    this.displayFields = [
      ...this.selectedFields,
      ...(serviceCountField ? [serviceCountField] : []),
      ...detailFields,
    ];
  }

  protected includeServiceDetailChanged() {
    localStorage.setItem(
      ServiceShrinkUptakeReport.INCLUDE_SERVICE_DETAIL_KEY,
      String(this.includeServiceDetail),
    );
    void this.getShrinkUptakeReport();
  }

  private getFieldLabel(field: ShrinkUptakeField) {
    const translated = this.t.tr(field.title);
    return field.rawTitle ?? translated ?? field.title;
  }

  private isServiceDetailField(fieldName: string) {
    return fieldName === 'ServiceCount' || /^Service\d+(MeasuredDate|CircumferenceMeasured|ImpregnationType)$/.test(fieldName);
  }

  private getExportVisibleFields() {
    const baseFields = this.includeServiceDetail ? this.displayFields : this.selectedFields;

    if (!this.includeServiceDetail) {
      return baseFields;
    }

    const nonDetail = baseFields.filter((f) => !this.isServiceDetailField(f.field));
    const serviceCount = baseFields.filter((f) => f.field === 'ServiceCount');
    const detail = baseFields
      .filter((f) => /^Service\d+(MeasuredDate|CircumferenceMeasured|ImpregnationType)$/.test(f.field))
      .sort((a, b) => {
        const aMatch = /^Service(\d+)(MeasuredDate|CircumferenceMeasured|ImpregnationType)$/.exec(a.field);
        const bMatch = /^Service(\d+)(MeasuredDate|CircumferenceMeasured|ImpregnationType)$/.exec(b.field);
        if (!aMatch || !bMatch) return a.field.localeCompare(b.field);

        const aIndex = parseInt(aMatch[1], 10);
        const bIndex = parseInt(bMatch[1], 10);
        if (aIndex !== bIndex) return aIndex - bIndex;

        const order: Record<string, number> = {
          MeasuredDate: 0,
          CircumferenceMeasured: 1,
          ImpregnationType: 2,
        };

        return (order[aMatch[2]] ?? 999) - (order[bMatch[2]] ?? 999);
      });

    return [...nonDetail, ...serviceCount, ...detail];
  }

  private getAllExportFields() {
    const baseFields = this.fields.filter((f) =>
      f.field !== ServiceShrinkUptakeReport.INCLUDE_SERVICE_DETAIL_FIELD
      && !this.isServiceDetailField(f.field),
    );

    if (!this.includeServiceDetail) {
      return baseFields;
    }

    return [
      ...baseFields,
      {
        field: 'ServiceCount',
        title: 'net.numberofservices',
      },
    ];
  }

  protected updateSelectList() {
    const includeServiceDetailField = this.fields.find(
      (f) => f.field === ServiceShrinkUptakeReport.INCLUDE_SERVICE_DETAIL_FIELD,
    );
    this.includeServiceDetail = Boolean(includeServiceDetailField?.selected);
    localStorage.setItem(
      ServiceShrinkUptakeReport.INCLUDE_SERVICE_DETAIL_KEY,
      String(this.includeServiceDetail),
    );

    this.refreshSelectedFields();
    localStorage.setItem(
      SHRINK_UPTAKE_SELECTED_COLUMNS_KEY,
      JSON.stringify(this.selectedFields.map((f) => f.field)),
    );
    void this.getShrinkUptakeReport();
  }

  protected sortTable(field: ShrinkUptakeField) {
    this.fm.setOrderByColumn(field.field);
  }

  protected async getShrinkUptakeReport() {
    // Only request the columns the user actually picked. The backend will
    // always include NetId for row identity even when omitted here.
    const cols = this.selectedFields.map((f) => f.field);

    try {
      this.ready = false;
      const res = await this.serviceReportService.getServiceShrinkUptakeReport(
        this.fm.queryFilter,
        cols,
        this.includeServiceDetail,
      );
      this.tableData = res.data ?? [];
      this.refreshDisplayFields();
      const total = res.headers?.get('x-total-count');
      this.fm.paginationTotalRecords = total ? parseInt(total, 10) : this.tableData.length;
    } catch (err) {
      this.errorService.handleError(err);
    } finally {
      this.ready = true;
    }
  }

  /**
   * Export the report to Excel.
   * @param onlyVisible When true, only the columns currently picked in the
   *   table are exported. When false, every column in the allow-list is
   *   exported regardless of what is currently visible.
   */
  protected async exportReport(onlyVisible: boolean) {
    if (this.exportProgress) return;
    this.exportProgress = true;
    try {
      const exportFields = onlyVisible ? this.getExportVisibleFields() : this.getAllExportFields();
      const cols = exportFields.map((f) => f.field);
      const labels = exportFields.map((f) => this.getFieldLabel(f));
      await this.serviceReportService.getServiceShrinkUptakeReportExport(
        this.fm.queryFilter,
        cols,
        labels,
        this.includeServiceDetail,
      );
    } catch (err) {
      this.errorService.handleError(err);
    } finally {
      this.exportProgress = false;
    }
  }
}